import { useEffect, useState } from "react";

import clsx from "clsx";
import { format } from "date-fns-tz";
import { eq, difference } from "lodash";
import { useToasts } from "react-toast-notifications";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { buildDefaultFileName } from "@features/data-export/components/data-export-builder-modal";
import { RecipientsSelector } from "@features/data-export/components/schedule-configurator/recipients-selector";
import { SubmitScheduledExportButtonWithConfirmationModal } from "@features/data-export/components/schedule-configurator/submit-scheduled-export-button-with-confirmation-modal";
import { type DataExportTarget } from "@features/data-export/types";
import {
  UPDATE_SCHEDULED_EXPORT_RECIPIENTS,
  RUN_TEST_SCHEDULED_EXPORT,
  FETCH_SCHEDULED_EXPORTS,
  FETCH_SCHEDULED_EXPORT_DETAILS,
} from "@gql/data-export";
import { useFeatureFlags, useNorthbeamMutation } from "@hooks/http";
import { useTextInEllipsis } from "@hooks/use-text-in-ellipsis";
import {
  type FetchScheduledExports_me_scheduledExports_data,
  type UpdateScheduledExportRecipients,
  type UpdateScheduledExportRecipientsVariables,
  type RunTestScheduledExport,
  type RunTestScheduledExportVariables,
} from "@north-beam/nb-client-side";
import { DataExportMethod } from "@north-beam/nb-client-side";
import { IconButton, SecondaryButton } from "@shared/buttons";

import { EditScheduledExportButtonWithModal } from "./edit-scheduled-export-button-with-modal";
import { formatCadence, getDestinationNameFromExportMethod } from "./utils";

const TARGET_LABELS: Record<string, string> = {
  PERFORMANCE_METRICS: "Sales Attribution",
  TOUCHPOINTS: "Touchpoints Attribution",
} as const;

interface Props {
  row: FetchScheduledExports_me_scheduledExports_data;
  emailRecipients: string[];
  onDelete(row: FetchScheduledExports_me_scheduledExports_data): void;
}

export function TableRow({ row, emailRecipients, onDelete }: Props) {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { textRef: scheduleNameRef, hasEllipsis: showTooltipForScheduleName } =
    useTextInEllipsis<HTMLTableDataCellElement>();

  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();
  const { addToast } = useToasts();

  const [
    updateScheduledExportRecipients,
    { loading: isUpdatingScheduledExportRecipients },
  ] = useNorthbeamMutation<
    UpdateScheduledExportRecipients,
    UpdateScheduledExportRecipientsVariables
  >(UPDATE_SCHEDULED_EXPORT_RECIPIENTS, {
    refetchQueries: [
      { query: FETCH_SCHEDULED_EXPORTS, variables: { page: 1 } },
      { query: FETCH_SCHEDULED_EXPORT_DETAILS, variables: { id: row.id } },
    ],
  });

  const [runTestScheduledExport, { loading: isRunningTestExport }] =
    useNorthbeamMutation<
      RunTestScheduledExport,
      RunTestScheduledExportVariables
    >(RUN_TEST_SCHEDULED_EXPORT, {});

  const [recipients, setRecipients] = useState<string[]>(() => row.recipients);

  const getExternalUsersFromRecipientsList = () =>
    difference(recipients, emailRecipients);

  useEffect(() => {
    setRecipients(row.recipients);
  }, [row]);

  const isTouchpointsExport = row.target === "TOUCHPOINTS";

  // TODO update to hasFeatureFlag("enableTouchpointsExport") once we enable editing touchpoints exports
  const canManageTouchpointsExports = false;

  const canRunTestExport = hasFeatureFlag("enableTestScheduledExports");
  const canEditExports = hasFeatureFlag("editScheduledExports");

  const canEditCurrentExport =
    !isTouchpointsExport || canManageTouchpointsExports;

  return (
    <tr>
      <td className="text-xs text-center align-middle">
        {format(new Date(row.createdAt), "PPp")}
      </td>
      <td className="align-middle">
        {TARGET_LABELS[row.target] ?? row.target}
      </td>
      <td
        className="align-middle truncate text-ellipsis max-w-[125px]"
        ref={scheduleNameRef}
      >
        <GenericTooltip
          showTooltip={showTooltipForScheduleName}
          content={row.scheduleName}
          noInfoCircle
          hideArrow
          placement="top"
        >
          {row.scheduleName}
        </GenericTooltip>
      </td>
      <td className="align-middle">
        {row.exportFileName ??
          buildDefaultFileName(
            row.target as DataExportTarget,
            user.companyName,
          )}
      </td>
      <td className="align-middle">
        {formatCadence(row.scheduleFrequency, row.schedules)}
      </td>
      <td className="text-center align-middle">
        {getDestinationNameFromExportMethod(row.exportMethod)}
      </td>
      <td className="w-6/12 align-middle">
        {row.exportMethod === DataExportMethod.NORTHBEAM_GCS_BUCKET ? (
          <>
            <RecipientsSelector
              allowCreatingOptions={hasFeatureFlag(
                "enableDataExportToExternalUsers",
              )}
              initialOptions={emailRecipients.map((recipient) => ({
                label: recipient,
                value: recipient,
              }))}
              tags={recipients.map((recipient) => ({
                label: recipient,
                value: recipient,
              }))}
              onChange={(selectedRecipients) => {
                setRecipients(
                  selectedRecipients.map((recipient) => recipient.value),
                );
              }}
            />
            {!eq(row.recipients, recipients) && (
              <div className="flex flex-wrap mt-3">
                <SecondaryButton
                  isDisabled={isUpdatingScheduledExportRecipients}
                  className="mr-3"
                  onClick={() => setRecipients(row.recipients)}
                >
                  Discard changes
                </SecondaryButton>
                {recipients.length > 0 && (
                  <SubmitScheduledExportButtonWithConfirmationModal
                    buttonLabel="Save"
                    externalUsers={getExternalUsersFromRecipientsList()}
                    isLoading={isUpdatingScheduledExportRecipients}
                    isDisabled={isUpdatingScheduledExportRecipients}
                    onSubmit={async () => {
                      prettyLogEventPage({
                        domain: "DataExport",
                        action: "onClick",
                        additionalActionInfo:
                          "Data export management - scheduled export updated",
                        data: {
                          pageContext: { id: row.id },
                        },
                      });

                      await updateScheduledExportRecipients({
                        variables: { id: row.id, recipients },
                      });
                    }}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <span className="text-[#999] text-xs">
            Adding recipients to external bucket export is not available.
          </span>
        )}
      </td>
      <td className="align-middle">
        <div className="flex flex-col flex-wrap justify-center">
          {canEditExports && (
            <GenericTooltip
              showTooltip={!canEditCurrentExport}
              content={
                <span>
                  <b>Coming soon!</b>
                  <br />
                  Touchpoints exports cannot be edited directly.
                  <br />
                  Delete and create a new touchpoints export instead.
                </span>
              }
              noInfoCircle
              hideArrow
              placement="top"
            >
              <EditScheduledExportButtonWithModal
                key={row.updatedAt}
                disabled={isRunningTestExport || !canEditCurrentExport}
                scheduledExportId={row.id}
              />
            </GenericTooltip>
          )}
          <IconButton
            leftIcon={<i className="fa-regular fa-sharp fa-trash" />}
            type="button"
            className={clsx("btn-outline-danger w-[5rem]", {
              "hover:w-full transition-[width]":
                canRunTestExport || canEditExports,
            })}
            size="sm"
            onClick={() => onDelete(row)}
          >
            Delete
          </IconButton>
          {canRunTestExport && (
            <IconButton
              loading={isRunningTestExport}
              disabled={isRunningTestExport}
              leftIcon={
                <i className="fa-regular fa-sharp fa-calendar-exclamation" />
              }
              type="button"
              className="btn-outline-primary w-fit"
              size="sm"
              onClick={async () => {
                prettyLogEventPage({
                  domain: "DataExport",
                  action: "onClick",
                  additionalActionInfo:
                    "Data export management - run test export",
                  data: {
                    pageContext: { id: row.id },
                  },
                });

                await runTestScheduledExport({ variables: { id: row.id } });

                addToast(
                  `Export ran successfully for report: ${row.scheduleName}`,
                  {
                    appearance: "success",
                    autoDismiss: true,
                  },
                );
              }}
            >
              Run Export Now
            </IconButton>
          )}
        </div>
      </td>
    </tr>
  );
}
